import { useCallback, useMemo } from 'react'
import { useAtomValue } from 'jotai'

import {
  invokeErrorByIdAtom,
  invokeErrorsAtom,
  isInvokingByIdAtom,
  isUndoingByIdAtom,
  pendingDisplayAtom,
  pendingStartByIdAtom,
  undoErrorByIdAtom,
  undoErrorsAtom,
  type InvokeErrorEntry,
  type UndoErrorEntry,
} from '../staged-action-local-state'
import {
  getActionStatus as deriveActionStatus,
  getErrorMessage as deriveErrorMessage,
  getPendingStartTime as deriveGetPendingStartTime,
  isUnread as deriveIsUnread,
  type ActionStatus,
  type ActionStatusInputs,
  type StagedActionStatusSubject,
} from '../staged-action-status'

export type StagedActionStatusGetter = (action: StagedActionStatusSubject) => ActionStatus

export interface StagedActionStatusMaps {
  invokeErrors: Map<string, InvokeErrorEntry>
  undoErrors: Map<string, UndoErrorEntry>
  pendingDisplay: Map<string, number>
}

export interface StagedActionStatusInfo {
  status: ActionStatus
  errorMessage: string | null
  pendingStartTime: string | null
  isUnread: boolean
  isInvoking: boolean
  isUndoing: boolean
}

export function getStatusInputsFromMaps(
  action: Pick<StagedActionStatusSubject, 'id'>,
  maps: StagedActionStatusMaps,
): ActionStatusInputs {
  return {
    invokeError: maps.invokeErrors.get(action.id),
    undoError: maps.undoErrors.get(action.id),
    pendingStart: maps.pendingDisplay.get(action.id),
  }
}

export function getActionStatusFromMaps(action: StagedActionStatusSubject, maps: StagedActionStatusMaps): ActionStatus {
  return deriveActionStatus(action, getStatusInputsFromMaps(action, maps))
}

/**
 * Status / error / pending-time derivations bound to the shared client-side
 * maps. Use for list-level transforms only; per-action cells should use
 * `useStagedActionStatus` so unrelated action updates don't wake them.
 */
export function useStagedActionDerivations() {
  const invokeErrors = useAtomValue(invokeErrorsAtom)
  const undoErrors = useAtomValue(undoErrorsAtom)
  const pendingDisplay = useAtomValue(pendingDisplayAtom)
  const statusMaps = useMemo(
    () => ({ invokeErrors, undoErrors, pendingDisplay }),
    [invokeErrors, undoErrors, pendingDisplay],
  )

  const getStatusInputs = useCallback(
    (action: StagedActionStatusSubject): ActionStatusInputs => getStatusInputsFromMaps(action, statusMaps),
    [statusMaps],
  )
  const getActionStatus = useCallback(
    (action: StagedActionStatusSubject): ActionStatus => deriveActionStatus(action, getStatusInputs(action)),
    [getStatusInputs],
  )
  const getErrorMessage = useCallback(
    (action: StagedActionStatusSubject): string | null => deriveErrorMessage(action, getStatusInputs(action)),
    [getStatusInputs],
  )
  const getPendingStartTime = useCallback(
    (action: StagedActionStatusSubject): string | null => deriveGetPendingStartTime(pendingDisplay.get(action.id)),
    [pendingDisplay],
  )
  const isUnread = useCallback((action: StagedActionStatusSubject) => deriveIsUnread(action), [])

  return { getActionStatus, getErrorMessage, getPendingStartTime, isUnread }
}

/**
 * Per-action status for a single cell. Subscribes only to this action's slice
 * of the shared maps (via the per-id selector atoms), so a status change for
 * one action never re-renders sibling cells.
 */
export function useStagedActionStatus(action: StagedActionStatusSubject): StagedActionStatusInfo {
  const id = action.id
  const invokeError = useAtomValue(invokeErrorByIdAtom(id))
  const undoError = useAtomValue(undoErrorByIdAtom(id))
  const pendingStart = useAtomValue(pendingStartByIdAtom(id))
  const isInvoking = useAtomValue(isInvokingByIdAtom(id))
  const isUndoing = useAtomValue(isUndoingByIdAtom(id))

  const inputs = { invokeError, undoError, pendingStart }
  return {
    status: deriveActionStatus(action, inputs),
    errorMessage: deriveErrorMessage(action, inputs),
    pendingStartTime: deriveGetPendingStartTime(pendingStart),
    isUnread: deriveIsUnread(action),
    isInvoking,
    isUndoing,
  }
}
